/**
 * FormDetailPage.tsx — Módulo Formularios. Detail Page Pattern copiado de
 * ProjectDetailPage: subnav con back + separador + status pill + tabs
 * centradas + prev/next, contenido debajo. Pestañas: Campos (editable, como
 * el brief de un proyecto), Disponibilidad, Respuestas, Incrustar (link
 * público + dominios + snippet — antes mezclado con Campos), Vista previa
 * (iframe del link público real).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  FORM_THEME_DEFAULTS, FORM_BG_TRANSPARENT, FORM_CONTRAST_AA, contrastRatio,
  isTransparentBg, resolveFormBg,
  type FormDef, type FormFieldDef, type FormSubmissionSummary,
} from '@evo-hub/shared';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { SubRow, SubRowList } from '../components/ui/SubRow.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { Input } from '../components/ui/Input.js';
import { Tabs } from '../components/ui/Tabs.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { KVGrid } from '../components/ui/KVGrid.js';
import { KVRow } from '../components/ui/KVRow.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { useFunnels } from '../hooks/useFunnels.js';
import { Toggle } from '../components/ui/Toggle.js';
import { Field } from '../components/ui/Field.js';
import { SegmentedControl } from '../components/ui/SegmentedControl.js';
import { FileUploadZone } from '../components/ui/FileUploadZone.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { Badge } from '../components/ui/Badge.js';
import { Icon } from '../components/Icon.js';
import { Pressable } from '../components/ui/Pressable.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { Modal } from '../components/ui/Modal.js';
import { Pill } from '../components/PillGroup.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { useToast } from '../components/ui/Toast.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { LinkToContactModal } from '../components/contacts/LinkToContactModal.js';
import { FormFieldEditor } from '../components/forms/FormFieldEditor.js';
import { AvailabilitySection } from '../components/forms/AvailabilitySection.js';
import { FormPreviewDock } from '../components/forms/FormPreviewDock.js';
import { FormClaudePanel } from '../components/forms/FormClaudePanel.js';
import { parseServerDate } from '../lib/date.js';
import {
  useForm, useForms, useUpdateForm, useDeleteForm, useSaveFormFields,
  useFormDomains, useAddFormDomain, useRemoveFormDomain,
  useFormSubmissions, useFormSubmission, useMarkSubmissionReviewed,
  useDismissSubmissions, useUndismissSubmissions, useLinkSubmissionsToContact,
  useCreateContactsFromSubmissions, useCreateLeadsFromSubmissions, useUnlinkSubmissions,
  useBulkSetSubmissionsRead,
  type FieldInput,
} from '../hooks/useForms.js';

type Tab = 'fields' | 'availability' | 'submissions' | 'embed';
type RowState = 'pending' | 'dismissed' | 'triaged' | 'auto';

const TAB_PARAM: Record<Tab, string> = {
  fields: 'campos', availability: 'disponibilidad', submissions: 'respuestas',
  embed: 'incrustar',
};

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return parseServerDate(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function toFieldInputs(fields: FormFieldDef[]): FieldInput[] {
  return fields.map(f => ({ label: f.label, fieldType: f.fieldType, required: f.required, options: f.options, placeholder: f.placeholder, width: f.width }));
}

function rowState(s: FormSubmissionSummary): RowState {
  if (s.contactDecision === 'dismissed') return 'dismissed';
  if (s.contactDecision === 'linked' || s.contactDecision === 'contact_created' || s.contactDecision === 'lead_created') return 'triaged';
  if (s.contactId == null) return 'pending';
  return 'auto';
}

export function FormDetailPage() {
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const toast = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [tab, setTab] = useState<Tab>(() => {
    const raw = searchParams.get('tab');
    if (raw === 'respuestas') return 'submissions';
    if (raw === 'disponibilidad') return 'availability';
    if (raw === 'incrustar') return 'embed';
    return 'fields';
  });

  const { data, isLoading } = useForm(id);
  const { data: funnelsData } = useFunnels();
  const funnels = funnelsData?.funnels ?? [];
  const updateForm = useUpdateForm();
  const deleteForm = useDeleteForm();
  const saveFields = useSaveFormFields();

  const [localFields, setLocalFields] = useState<FieldInput[] | null>(null);
  useEffect(() => {
    if (data) setLocalFields(toFieldInputs(data.fields));
  }, [data]);

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({ title: data?.form.name ?? t('forms.list.pageTitle'), });
    return () => setOverride({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo actualizar al cambiar nombre real
  }, [setOverride, t, data?.form.name]);

  // Navegación prev/next — mismo mecanismo que ProjectDetailPage: preferir
  // el state pasado por la lista (respeta el filtro/orden que Eduardo vio),
  // fallback a la lista completa para entrada directa por URL.
  const locState = (location.state as { filteredIds?: string[] } | null) ?? {};
  const stateIds: string[] = locState.filteredIds ?? [];
  const { data: allFormsData } = useForms();
  const fallbackIds = (allFormsData?.forms ?? []).map(f => f.id);
  const filteredIds = stateIds.length > 0 ? stateIds : fallbackIds;
  const currentIndex = id ? filteredIds.indexOf(id) : -1;
  const prevId = currentIndex > 0 ? filteredIds[currentIndex - 1] : null;
  const nextId = currentIndex < filteredIds.length - 1 ? filteredIds[currentIndex + 1] : null;
  const showNav = filteredIds.length > 1 && currentIndex >= 0;

  function navTo(targetId: string) {
    navigate(`/formularios/${targetId}`, { state: location.state });
  }

  if (isLoading || !data || !localFields) return <LoadingState fullHeight />;

  const { form } = data;
  const dirty = JSON.stringify(localFields) !== JSON.stringify(toFieldInputs(data.fields));
  const publicUrl = `${window.location.origin}/f/${form.slug}`;

  // La vista previa es un iframe del link público real: para que refleje un
  // cambio recién guardado (tema, logo o campos) hay que remontarlo. Ambos
  // insumos vienen de la query, que se invalida en cada save — así el iframe
  // se recarga solo y nunca queda mostrando la versión vieja.
  const previewKey = `${form.updatedAt}|${form.logoPath ?? ''}|${JSON.stringify(data.fields)}`;

  const tabs = [
    { value: 'fields' as const, label: t('forms.detail.tabFields') },
    { value: 'availability' as const, label: t('forms.detail.tabAvailability') },
    { value: 'submissions' as const, label: t('forms.detail.tabSubmissions') },
    { value: 'embed' as const, label: t('forms.detail.tabEmbed') },
  ];

  function changeTab(next: Tab) {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', TAB_PARAM[next]);
    setSearchParams(params);
  }

  function handleCopyLink() {
    void navigator.clipboard.writeText(publicUrl).then(() => toast.info(t('forms.detail.linkCopied')));
  }

  // Con respuestas se pregunta con el número a la vista y se borran con el
  // formulario (el servidor guarda una copia antes). Antes el servidor lo
  // rechazaba con 409 y la página no escuchaba el error: el clic no hacía
  // nada visible (Eduardo, 2026-10-12, un formulario de demo imborrable).
  async function handleDelete() {
    const respuestas = allFormsData?.forms.find(f => f.id === form.id)?.submissionCount ?? 0;
    const ok = respuestas > 0
      ? await confirm({
          title: t('forms.detail.deleteWithSubmissionsTitle', { count: respuestas }),
          description: t('forms.detail.deleteWithSubmissionsBody'),
          confirmLabel: t('forms.detail.deleteWithSubmissions'),
          tone: 'danger',
        })
      : await confirm({ title: t('forms.detail.deleteConfirm'), tone: 'danger' });
    if (!ok) return;
    deleteForm.mutate({ id: form.id, withSubmissions: respuestas > 0 }, {
      onSuccess: () => { toast.success(t('forms.detail.deleted')); navigate('/formularios'); },
      onError: () => { toast.error(t('forms.detail.deleteError')); },
    });
  }

  function handleSaveFields() {
    if (!localFields) return;
    saveFields.mutate({ id: form.id, fields: localFields }, {
      onSuccess: () => toast.info(t('forms.fields.saved')),
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {confirmDialog}
      <FormClaudePanel form={form} section={tab} />

      {/* SubNav + Tabs — misma estructura que ProjectDetailPage */}
      <div style={{ height: 'var(--evo-subnav-h)', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0, background: 'var(--evo-bg)' }}>
        <div style={{ padding: '0 var(--evo-page-pad-x)', height: '100%' }}>
        <div style={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', flexShrink: 0, paddingRight: 'var(--evo-space-4)' }}>
            <Button
              variant="link" tone="muted" size="sm"
              icon={<Icon name="chevron-left" size={14} />}
              onClick={() => navigate('/formularios')}
            >
              {t('forms.detail.back')}
            </Button>
            <div style={{ width: '1px', height: 'var(--evo-divider-h)', background: 'var(--evo-stroke)', flexShrink: 0 }} />
          </div>

          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
            <Tabs<Tab> tabs={tabs} value={tab} onChange={changeTab} noBorder />
          </div>

          {/* Derecha: estado del registro + navegación (patrón V2) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexShrink: 0, paddingLeft: 'var(--evo-space-4)' }}>
            <Pill variant="outline" tone={form.enabled ? 'cyan' : 'muted'} size="sm">
              {form.enabled ? t('forms.list.statusEnabled') : t('forms.list.statusDisabled')}
            </Pill>
            {showNav && (
              <>
                <IconButton
                  variant="outline" tone="muted" size="md" disabled={!prevId}
                  icon={<Icon name="chevron-left" size={15} />}
                  ariaLabel={t('forms.detail.prev')}
                  tooltip={t('forms.detail.prev')}
                  onClick={() => prevId && navTo(prevId)}
                />
                <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', whiteSpace: 'nowrap' }}>
                  {t('forms.detail.navPosition', { current: currentIndex + 1, total: filteredIds.length })}
                </span>
                <IconButton
                  variant="outline" tone="muted" size="md" disabled={!nextId}
                  icon={<Icon name="chevron-right" size={15} />}
                  ariaLabel={t('forms.detail.next')}
                  tooltip={t('forms.detail.next')}
                  onClick={() => nextId && navTo(nextId)}
                />
              </>
            )}
          </div>

        </div>
        </div>
      </div>

      {/* Contenido — Respuestas maneja su propio scroll + SelectionActionBar a
          page root (REGLA #7); el resto comparte un único scroll region. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {tab === 'submissions' ? (
          <SubmissionsPanel formId={form.id} initialSelectedId={searchParams.get('selected')} />
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>

            {tab === 'fields' && (
              <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
              <div style={{ paddingTop: 'var(--evo-space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>

                {/* Campos y vista previa en la MISMA pantalla: antes la vista
                    previa era una pestaña aparte y había que ir, mirar y
                    volver para saber qué hizo un cambio.
                    Mitad y mitad: con 380px fijos la vista previa quedaba a un
                    cuarto del ancho y el formulario se veía en miniatura, que
                    es justo lo que esta columna existe para evitar. En pantalla
                    angosta se apilan. */}
                <div className="evo-form-fields-grid" style={{ display: 'grid', gap: 'var(--evo-related-gap)', alignItems: 'start' }}>
                <DetailSection
                  title={t('forms.fields.sectionTitle')}
                  divider={false}
                  titleRight={
                    <Button variant="solid" tone="cyan" size="sm" disabled={!dirty} loading={saveFields.isPending} onClick={handleSaveFields}>
                      {t('forms.fields.saveBtn')}
                    </Button>
                  }
                >
                  <FormFieldEditor fields={localFields} onChange={setLocalFields} />
                </DetailSection>
                <FormPreviewDock publicUrl={publicUrl} previewKey={previewKey} />
                </div>

                {/* Fila 2 — Ajustes: cuatro filas KV no merecen una tira de
                    1200px; media columna. */}
                <div className="evo-duo-grid">
                <DetailSection title={t('forms.detail.settingsSection')} divider={false}>
                  <KVGrid>
                    <KVRow label={t('forms.detail.nameLabel')} value={form.name} onSave={async v => { await updateForm.mutateAsync({ id: form.id, name: v }); }} />
                    <KVRow label={t('forms.detail.descriptionLabel')} value={form.description ?? ''} placeholder="—" onSave={async v => { await updateForm.mutateAsync({ id: form.id, description: v || null }); }} />
                    <KVRow label={t('forms.detail.submitLabelLabel')} value={form.submitLabel ?? ''} placeholder="Enviar" onSave={async v => { await updateForm.mutateAsync({ id: form.id, submitLabel: v || null }); }} />
                    <KVRow label={t('forms.detail.thankYouLabel')} value={form.thankYouMessage ?? ''} placeholder="—" onSave={async v => { await updateForm.mutateAsync({ id: form.id, thankYouMessage: v || null }); }} />
                    {/* Embudo: prendido, cada respuesta entra como carta en la
                        primera columna. El vínculo vive acá y en ningún otro
                        lado — el editor de etapas no lo repite. */}
                    <KVRow
                      label={t('forms.detail.funnelLabel')}
                      value={funnels.find(f => f.id === form.funnelId)?.name ?? t('forms.detail.funnelNone')}
                      renderValue={() => (
                        <Dropdown<number>
                          value={form.funnelId ?? 0}
                          options={[
                            { value: 0, label: t('forms.detail.funnelNone') },
                            ...funnels.map(f => ({ value: f.id, label: f.name })),
                          ]}
                          onChange={v => { void updateForm.mutateAsync({ id: form.id, funnelId: v === 0 ? null : v }); }}
                          size="sm"
                          align="right"
                        />
                      )}
                      last
                    />
                  </KVGrid>
                  {form.funnelId != null && (
                    <p style={{ marginTop: 'var(--evo-space-2)', marginBottom: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)' }}>
                      {t('forms.detail.funnelHelp')}
                    </p>
                  )}
                  <div style={{ marginTop: 'var(--evo-space-4)' }}>
                    <Button variant="outline" tone="danger" size="sm" onClick={handleDelete}>{t('forms.detail.deleteBtn')}</Button>
                  </div>
                </DetailSection>
                </div>

              </div>
              </div>
            )}

            {tab === 'availability' && (
              <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
              <div style={{ paddingTop: 'var(--evo-space-6)' }}>
                <AvailabilitySection formId={form.id} />
              </div>
              </div>
            )}

            {tab === 'embed' && (
              <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
              <div style={{ paddingTop: 'var(--evo-space-6)' }}>

                {/* Enlace público | Insertar en un sitio — dos tarjetas
                    parejas; la vista previa vive en Apariencia. */}
                <div className="evo-duo-grid">
                <DetailSection title={t('forms.detail.publicLink', { defaultValue: 'Enlace público' })} divider={false}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
                    <code style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-cyan)', background: 'var(--evo-bg-2)', padding: '6px 10px', borderRadius: 'var(--evo-r)', wordBreak: 'break-all' }}>{publicUrl}</code>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
                      <Button variant="outline" tone="muted" size="sm" onClick={handleCopyLink} type="button">{t('forms.detail.copyLink')}</Button>
                      <a href={publicUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                        <Button variant="outline" tone="muted" size="sm" type="button">{t('forms.detail.openLink')}</Button>
                      </a>
                      <div style={{ flex: 1 }} />
                      <Toggle checked={form.enabled} onChange={v => updateForm.mutate({ id: form.id, enabled: v })} label={t('forms.detail.enabledLabel')} size="sm" />
                    </div>
                  </div>
                </DetailSection>

                <DetailSection title={t('forms.detail.tabEmbed')} divider={false}>
                  <EmbedSection formId={form.id} slug={form.slug} name={form.name} publicUrl={publicUrl} />
                </DetailSection>
                </div>

              </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

// ── Apariencia (personalización visual POR formulario) ──────────────────────────

// Un color se persiste al soltar el picker (onBlur), no en cada movimiento
// del cursor — arrastrar el selector dispararía un PATCH por frame.
// ── Vista previa (iframe same-origin del link público) ──────────────────────────

// Acordeón simple, header + body con transición grid-template-rows — mismo
// técnica que RevisionsLedger.tsx (projects), sin primitive nuevo.
function EmbedAccordion({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(true);

  return (
    <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)' }}>
      <Pressable fullWidth ariaExpanded={open} onPress={() => setOpen(o => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-4)' }}>
          <span style={{
            display: 'inline-flex', transform: open ? 'none' : 'rotate(-90deg)',
            transition: 'transform 200ms ease-out', flexShrink: 0,
          }}>
            <Icon name="chevron-down" size={12} />
          </span>
          <span style={{
            flex: 1, textAlign: 'left', fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)',
            letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-text-muted)',
            fontFamily: 'var(--evo-font)',
          }}>
            {title}
          </span>
        </div>
      </Pressable>
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 200ms ease-out' }}>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ padding: '0 var(--evo-space-4) var(--evo-space-4)' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// La vista previa vive en FormPreviewDock, al lado de los campos.

// ── Incrustable (Etapa 2) ────────────────────────────────────────────────────

function buildSnippet(slug: string, publicUrl: string, name: string): string {
  const iframeId = `evo-form-${slug}`;
  return `<iframe id="${iframeId}" src="${publicUrl}" style="width:100%;border:0;display:block;min-height:400px" title="${name}"></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e && e.data && e.data.source === 'evo-form') {
    var f = document.getElementById('${iframeId}');
    if (f) f.style.height = e.data.height + 'px';
  }
});
</script>`;
}

function EmbedSection({ formId, slug, name, publicUrl }: { formId: string; slug: string; name: string; publicUrl: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [domainInput, setDomainInput] = useState('');

  const { data } = useFormDomains(formId);
  const addDomain = useAddFormDomain(formId);
  const removeDomain = useRemoveFormDomain(formId);
  const domains = data?.domains ?? [];

  function handleAddDomain() {
    const value = domainInput.trim();
    if (!value) return;
    addDomain.mutate(value, {
      onSuccess: () => setDomainInput(''),
      onError: err => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  const snippet = buildSnippet(slug, publicUrl, name);
  function handleCopySnippet() {
    void navigator.clipboard.writeText(snippet).then(() => toast.info(t('forms.detail.snippetCopied')));
  }

  return (
    <div className="evo-info-grid">
      <div>
        <div style={{ marginBottom: 'var(--evo-space-1)', fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-semibold)', color: 'var(--evo-fg-2)' }}>{t('forms.detail.domainsLabel')}</div>
        <p style={{ fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', marginBottom: 'var(--evo-space-3)' }}>{t('forms.detail.domainsHint')}</p>
        {domains.length > 0 ? (
          <div style={{ marginBottom: 'var(--evo-space-3)' }}>
            <SubRowList>
              {domains.map(d => (
                <SubRow
                  key={d.id}
                  title={d.domain}
                  right={<IconButton icon={<Icon name="close" size={12} />} ariaLabel={t('common.delete', 'Eliminar')} variant="ghost" tone="danger" size="sm" onClick={() => removeDomain.mutate(d.id)} />}
                />
              ))}
            </SubRowList>
          </div>
        ) : (
          <p style={{ fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', marginBottom: 'var(--evo-space-3)' }}>{t('forms.detail.domainsEmpty')}</p>
        )}
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)' }}>
          <Input
            type="text" size="sm" value={domainInput} onChange={setDomainInput}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
            placeholder={t('forms.detail.domainPlaceholder')}
          />
          <Button type="button" variant="outline" tone="muted" size="sm" loading={addDomain.isPending} onClick={handleAddDomain}>
            {t('forms.detail.addDomain')}
          </Button>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--evo-space-2)' }}>
          <span style={{ fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-semibold)', color: 'var(--evo-fg-2)' }}>{t('forms.detail.snippetLabel')}</span>
          <Button type="button" variant="outline" tone="muted" size="sm" onClick={handleCopySnippet}>{t('forms.detail.copySnippet')}</Button>
        </div>
        {/* El snippet hace wrap — nada de scroll horizontal en esta caja. */}
        <pre style={{
          fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font-mono)', color: 'var(--evo-fg-2)',
          background: 'var(--evo-bg-2)', padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)',
          whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0,
        }}>
          {snippet}
        </pre>
      </div>
    </div>
  );
}

// ── Respuestas ────────────────────────────────────────────────────────────────

function SubmissionsPanel({ formId, initialSelectedId }: { formId: string; initialSelectedId: string | null }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading } = useFormSubmissions(formId);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [openId, setOpenId] = useState<number | null>(initialSelectedId ? parseInt(initialSelectedId, 10) : null);
  const [showLinkModal, setShowLinkModal] = useState(false);

  const dismiss = useDismissSubmissions(formId);
  const undismiss = useUndismissSubmissions(formId);
  const link = useLinkSubmissionsToContact(formId);
  const createContacts = useCreateContactsFromSubmissions(formId);
  const createLeads = useCreateLeadsFromSubmissions(formId);
  const unlink = useUnlinkSubmissions(formId);
  const setRead = useBulkSetSubmissionsRead(formId);

  const submissions = data?.submissions ?? [];

  function toggleSelect(id: number) {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }
  function clearSelection() { setSelected(new Set()); }
  function handleError(err: unknown) { toast.error(err instanceof Error ? err.message : String(err)); }

  const selectedRows = submissions.filter(s => selected.has(s.id));
  const selectedStates = new Set(selectedRows.map(rowState));
  const showDismissedActions = selectedStates.size === 1 && selectedStates.has('dismissed');
  const showTriagedActions = selectedStates.size === 1 && selectedStates.has('triaged');
  // Las acciones de triage (vincular, crear contacto, crear lead) solo cuando
  // TODAS las elegidas están sin resolver. Antes bastaba con que no fueran
  // todas descartadas o todas triageadas, y desde que una respuesta ya
  // vinculada también se puede seleccionar eso ofrecería "crear contacto"
  // sobre algo que ya tiene uno. Leído/no leído queda siempre: no depende del
  // triage.
  const showPendingActions = selected.size > 0 && selectedStates.size === 1 && selectedStates.has('pending');

  return (
    <div className="flex flex-col flex-1" style={{ overflow: 'hidden' }}>
      {/* REGLA #7 — SelectionActionBar a page root, full-width, fuera del wrapper con padding/maxWidth de la pestaña Campos. */}
      <SelectionActionBar visible={selected.size > 0} countLabel={t('forms.submissions.selected', { count: selected.size })} onClearSelection={clearSelection}>
        {/* Leído / no leído: las mismas dos acciones que la lista de afuera. */}
        <SelectionActionBar.Action
          label={t('forms.list.bulk.markRead')}
          loading={setRead.isPending}
          onClick={() => setRead.mutate({ submissionIds: Array.from(selected), read: true }, {
            onSuccess: (r: { updated: number }) => { clearSelection(); toast.info(t('forms.list.bulk.markedRead', { count: r.updated })); },
            onError: handleError,
          })}
        />
        <SelectionActionBar.Action
          label={t('forms.list.bulk.markUnread')}
          loading={setRead.isPending}
          onClick={() => setRead.mutate({ submissionIds: Array.from(selected), read: false }, {
            onSuccess: (r: { updated: number }) => { clearSelection(); toast.info(t('forms.list.bulk.markedUnread', { count: r.updated })); },
            onError: handleError,
          })}
        />
        <SelectionActionBar.Divider />
        {showPendingActions && (
          <>
            <SelectionActionBar.Action label={t('forms.submissions.actions.link')} onClick={() => setShowLinkModal(true)} />
            <SelectionActionBar.Action
              label={t('forms.submissions.actions.createContact')} loading={createContacts.isPending} confirmRequired
              onClick={() => createContacts.mutate(Array.from(selected), {
                onSuccess: r => { clearSelection(); toast.info(t('forms.submissions.bulk.contactsCreated', { count: r.created.length })); },
                onError: handleError,
              })}
            />
            <SelectionActionBar.Action
              label={t('forms.submissions.actions.createLead')} loading={createLeads.isPending} confirmRequired
              onClick={() => createLeads.mutate(Array.from(selected), {
                onSuccess: r => { clearSelection(); toast.info(t('forms.submissions.bulk.leadsCreated', { count: r.created.length })); },
                onError: handleError,
              })}
            />
            <SelectionActionBar.Action
              label={t('forms.submissions.actions.dismiss')} loading={dismiss.isPending} confirmRequired
              onClick={() => dismiss.mutate(Array.from(selected), {
                onSuccess: r => { clearSelection(); toast.info(t('forms.submissions.bulk.dismissed', { count: r.dismissed })); },
                onError: handleError,
              })}
            />
          </>
        )}
        {showDismissedActions && (
          <SelectionActionBar.Action
            label={t('forms.submissions.actions.undismiss')} loading={undismiss.isPending}
            onClick={() => undismiss.mutate(Array.from(selected), {
              onSuccess: r => { clearSelection(); toast.info(t('forms.submissions.bulk.restored', { count: r.restored })); },
              onError: handleError,
            })}
          />
        )}
        {showTriagedActions && (
          <SelectionActionBar.Action
            label={t('forms.submissions.actions.unlink')} loading={unlink.isPending} confirmRequired
            onClick={() => unlink.mutate(Array.from(selected), {
              onSuccess: r => { clearSelection(); toast.info(t('forms.submissions.bulk.unlinked', { count: r.unlinked })); },
              onError: handleError,
            })}
          />
        )}
      </SelectionActionBar>

      {showLinkModal && (
        <LinkToContactModal
          count={selected.size}
          loading={link.isPending}
          onClose={() => setShowLinkModal(false)}
          onConfirm={contactId => link.mutate({ submissionIds: Array.from(selected), contactId }, {
            onSuccess: r => { setShowLinkModal(false); clearSelection(); toast.info(t('forms.submissions.bulk.linked', { count: r.linked })); },
            onError: handleError,
          })}
        />
      )}

      {/* Modelo del handoff (tarjeta "Envíos"): sub-filas compactas
          `Nombre — email | fecha | pill de estado`, conteo en el header.
          Un solo pill por fila: sin revisar > pendiente > descartado > contacto. */}
      <div className="flex-1 overflow-auto">
        <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
          <div style={{ paddingTop: 'var(--evo-space-6)', paddingBottom: 'var(--evo-space-6)' }}>
            <DetailSection
              title={t('forms.detail.tabSubmissions')}
              divider={false}
              titleRight={
                submissions.length > 0 ? (
                  <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
                    {t('forms.submissions.countLabel', { count: submissions.length })}
                    {submissions.some(s => !s.reviewedAt) ? ` · ${t('forms.submissions.unreviewedCount', { count: submissions.filter(s => !s.reviewedAt).length })}` : ''}
                  </span>
                ) : undefined
              }
            >
              {isLoading && <LoadingState size="sm" />}
              {!isLoading && submissions.length === 0 && <EmptyState size="md" reserveSpace title={t('forms.submissions.empty')} />}
              {/* Los mismos cortes de tiempo y la misma selección que la lista
                  de afuera: seleccionar toda una banda, contador de sin leer,
                  divisores del sistema. Antes era una lista suelta sin nada de
                  eso — la única del módulo que se comportaba distinto. */}
              {submissions.length > 0 && (
                <div style={{
                  // Los cotejos de la banda y los de las filas en la misma
                  // columna: la banda toma la sangría de SubRow, más el 1px de
                  // su borde — la banda no tiene y sin eso quedan corridos por
                  // un píxel, que con los cotejos uno debajo del otro se ve.
                  '--evo-band-inset': 'calc(var(--evo-subrow-px) + 1px)',
                } as React.CSSProperties}>
                <TimeBandedList<FormSubmissionSummary, number>
                  // Estilo de log, como Mensajería: el divisor sin fondo ni
                  // filetes y cada fila con su caja. Esta lista vive adentro de
                  // una tarjeta — la banda con fondo parecía un encabezado de
                  // tabla pegado a filas redondeadas.
                  plain
                  items={submissions}
                  getId={s => s.id}
                  getTimestamp={s => s.createdAt}
                  checkedIds={selected}
                  isUnread={s => !s.reviewedAt}
                  onBandCheck={(ids, allChecked) => setSelected(prev => {
                    const next = new Set(prev);
                    for (const id of ids) { if (allChecked) next.delete(id); else next.add(id); }
                    return next;
                  })}
                  renderItem={s => {
                    const state = rowState(s);
                    const name = s.submittedName ?? t('dmTriage.unidentified');
                    const email = s.submittedEmail ?? s.submittedPhone;
                    return (
                      <SubRow
                        onClick={() => setOpenId(s.id)}
                        // Toda respuesta se puede seleccionar. Antes las que ya
                        // tenían contacto (state 'auto') no llevaban casilla, y
                        // en un formulario donde todas están vinculadas no
                        // había ninguna que marcar — ni para leído/no leído,
                        // que no tiene nada que ver con el triage. Lo que
                        // cambia según el estado es qué ofrece la barra.
                        left={
                          <span onClick={e => { e.stopPropagation(); toggleSelect(s.id); }} style={{ display: 'inline-flex' }}>
                            <Checkbox checked={selected.has(s.id)} onChange={() => {}} />
                          </span>
                        }
                        title={
                          <span style={s.reviewedAt ? undefined : { fontWeight: 'var(--evo-fw-semibold)', color: 'var(--evo-fg-1)' }}>
                            {name}{email ? ` — ${email}` : ''}
                          </span>
                        }
                        meta={fmt(s.createdAt)}
                        right={
                          !s.reviewedAt ? <Pill tone="cyan" size="xs">{t('forms.submissions.unreviewed')}</Pill>
                          : state === 'pending' ? <Pill tone="amber" size="xs">{t('forms.submissions.pendingDecision')}</Pill>
                          : state === 'dismissed' ? <Pill tone="muted" size="xs">{t('forms.submissions.dismissedPill')}</Pill>
                          : s.contactId != null ? <Pill tone="muted" size="xs">{t('forms.submissions.linkedTo')} #{s.contactId}</Pill>
                          : undefined
                        }
                      />
                    );
                  }}
                />
                </div>
              )}
            </DetailSection>
          </div>
        </div>
      </div>

      {openId != null && <SubmissionDetailModal id={openId} formId={formId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function SubmissionDetailModal({ id, formId, onClose }: { id: number; formId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data } = useFormSubmission(id);
  const markReviewed = useMarkSubmissionReviewed(formId);
  const markedRef = useRef(false);

  useEffect(() => {
    if (data && !data.reviewedAt && !markedRef.current) {
      markedRef.current = true;
      markReviewed.mutate(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- marcar solo cuando llega la data de ESTE submission
  }, [data?.id]);

  const toast = useToast();

  function answerText(value: string | string[]): string {
    return Array.isArray(value) ? value.join(', ') : value;
  }

  // "Copiar texto": líneas `Etiqueta: valor` para pegar en cualquier documento.
  async function copyPlain() {
    if (!data) return;
    const text = data.answers.map(a => `${a.label}: ${answerText(a.value) || '—'}`).join('\n');
    await navigator.clipboard.writeText(text);
    toast.info(t('forms.submissions.detail.copied'));
  }

  // "Copiar tabla": text/html (tabla real al pegar en Docs/Word) + TSV como
  // plano de respaldo — el mismo clic sirve para hoja de cálculo.
  async function copyTable() {
    if (!data) return;
    const tsv = data.answers.map(a => `${a.label}\t${answerText(a.value) || '—'}`).join('\n');
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<table>${data.answers.map(a => `<tr><td><b>${esc(a.label)}</b></td><td>${esc(answerText(a.value) || '—')}</td></tr>`).join('')}</table>`;
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
        }),
      ]);
    } catch {
      await navigator.clipboard.writeText(tsv);
    }
    toast.info(t('forms.submissions.detail.copied'));
  }

  return (
    <Modal open onClose={onClose} title={t('forms.submissions.detail.title')} size="md">
      {!data ? <LoadingState size="sm" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)' }}>{t('forms.submissions.detail.submittedAt')}: {fmt(data.createdAt)}</p>
          <KVGrid columns={1}>
            {data.answers.map((a, i) => (
              <KVRow
                key={a.fieldId}
                label={a.label}
                value={answerText(a.value) || t('forms.submissions.detail.noAnswer')}
                last={i === data.answers.length - 1}
              />
            ))}
          </KVGrid>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--evo-space-2)', paddingTop: 'var(--evo-space-2)', borderTop: '1px solid var(--evo-stroke)' }}>
            <Button variant="outline" tone="muted" size="sm" onClick={() => { void copyPlain(); }}>
              {t('forms.submissions.detail.copyText')}
            </Button>
            <Button variant="outline" tone="muted" size="sm" onClick={() => { void copyTable(); }}>
              {t('forms.submissions.detail.copyTable')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
